import React, { useCallback, useState } from 'react';
import { Button, Tooltip } from '@notch-ordering/ui-components';
import TrashIcon from '@icons/trash-icon.svg';
import AddIcon from '@icons/add-icon.svg';
import { NotificationsRules } from '@/ar/network/AccountsReceivable.network';

export enum ERuleType {
    BEFORE_DUE_DATE = 'before_due_date',
    AFTER_DUE_DATE = 'after_due_date',
    ON_DUE_DATE = 'on_due_date',
}

export enum ENotificationType {
    BEFORE_DUE_DATE = 'beforeDueDate',
    AFTER_DUE_DATE = 'afterDueDate',
    ON_DUE_DATE = 'onDueDate',
}

interface PaymentReminderInputsProps {
    reminders: NotificationsRules[],
    notificationType: ENotificationType,
    label: string,
    placeholder: string,
    minValue?: number,
    maxValue?: number,
    onChange: (reminders: NotificationsRules[]) => void,
    addButtonLabel: string,
    isLoading: boolean,
    isRestoring: boolean,
    isSaving: boolean,
    maxReminderCount: number,
    validationErrors?: Record<ENotificationType, { type: string }[]>, // Add validation errors prop
}

const DeletePaymentReminderTooltip: React.FC<{ onClick: () => void }> = ({ onClick }) => {
    const [showDeleteTooltip, setShowDeleteTooltip] = useState<boolean>(false);

    return (
        <Tooltip
            show={showDeleteTooltip}
            placement="top"
            className="flex"
            tooltipClassName={'py-1 px-2 rounded-lg'}
            showArrow={false}
            onShow={(): void => { setShowDeleteTooltip(true); }}
            onHide={(): void => { setShowDeleteTooltip(false); }}
            trigger={
                <Button
                    variant="ICON"
                    size="ICON_SMALL"
                    className="ml-2 cursor-pointer"
                    data-testid="delete-reminder-button"
                    onClick={(): void => { onClick(); }}>
                    <div className="h-4 w-4 text-gray-600"><TrashIcon data-testid="trash-icon"/></div>
                </Button>
            }>
            {'Delete'}
        </Tooltip>
    );
};

const AddNewRuleButton: React.FC<{ onClick: () => void, isDisabled: boolean, label: string }> = ({ onClick, isDisabled, label }) => (
    <Button
        className="cursor-pointer"
        variant={'LINK'}
        size={'NO_BACKGROUND'}
        onClick={onClick}
        disabled={isDisabled}
        data-testid="add-reminder-button">
        <div className="-ml-2 flex gap-x-3 mt-0 items-center">
            <AddIcon className="w-4 h-4" />
            <span className="font-medium">{label}</span>
        </div>
    </Button>
);

export const PaymentReminderInputs: React.FC<PaymentReminderInputsProps> = ({
    reminders,
    notificationType,
    label,
    placeholder,
    minValue,
    maxValue,
    onChange,
    addButtonLabel,
    isLoading,
    isRestoring,
    isSaving,
    maxReminderCount,
    validationErrors
}) => {
    // Convert ENotificationType to ERuleType
    const getTypeFromNotificationType = (type: ENotificationType): ERuleType => {
        switch (type) {
            case ENotificationType.BEFORE_DUE_DATE:
                return ERuleType.BEFORE_DUE_DATE;
            case ENotificationType.AFTER_DUE_DATE:
                return ERuleType.AFTER_DUE_DATE;
            case ENotificationType.ON_DUE_DATE:
                return ERuleType.ON_DUE_DATE;
            default:
                return null;
        }
    };

    // Internal function to handle notification rule changes
    const handleNotificationRuleChange = useCallback((index: number, value: number) => {
        const updatedReminders = reminders.map((item, i) => {
            if (i === index) {
                return {
                    ...item,
                    interval: value,
                    type: getTypeFromNotificationType(notificationType) // Ensure type is set
                };
            }
            return item;
        });
        onChange(updatedReminders);
    }, [reminders, onChange, notificationType]);

    // Internal function to delete a payment reminder
    const deletePaymentReminder = useCallback((index: number) => {
        const updatedReminders = reminders.filter((_, i) => i !== index);
        onChange(updatedReminders);
    }, [reminders, onChange]);

    // Internal function to add a new payment reminder
    const addNewPaymentReminder = useCallback(() => {
        const newReminder = {
            type: getTypeFromNotificationType(notificationType),
            interval: 1 // Default value
        };
        onChange([...reminders, newReminder]);
    }, [reminders, onChange, notificationType]);

    // Check if a specific input has an error
    const hasError = (index: number) => validationErrors
               && validationErrors[notificationType]
               && validationErrors[notificationType][index]
               && validationErrors[notificationType][index].type;

    return (
        <>
            {reminders.map((reminder, index) => (
                <div key={index} className="flex flex-row mb-2">
                    <div className="ml-4">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            {label}
                        </label>
                        <input
                            type="number"
                            min={minValue}
                            max={maxValue}
                            className={`border ${hasError(index) ? 'border-red-500' : 'border-gray-300'} rounded px-2 py-1`}
                            placeholder={placeholder}
                            value={reminder.interval || ''}
                            onChange={(e) => {
                                const value = parseInt(e.target.value, 10);
                                if (!Number.isNaN(value)) {
                                    handleNotificationRuleChange(index, value);
                                }
                            }}/>
                        {hasError(index) && (
                            <div className="text-red-500 text-xs mt-1">
                                Please select a valid type
                            </div>
                        )}
                    </div>
                    {reminders.length > 1 && (
                        <DeletePaymentReminderTooltip
                            onClick={() => deletePaymentReminder(index)}/>
                    )}
                </div>
            ))}

            {!!reminders.length && (
                <AddNewRuleButton
                    onClick={addNewPaymentReminder}
                    label={addButtonLabel}
                    isDisabled={
                        isLoading
                        || isRestoring
                        || isSaving
                        || reminders.length === maxReminderCount
                    }/>
            )}
        </>
    );
};
